import { MicModeToggleVariant } from "../components/useroverlay/MicModeToggle";

export type WaveformMode = 'overlay' | 'replace';

export interface GUIThemeProperties {
  // Mic button behavior
  micStaticText: string | null;
  micVolatile: boolean;
  showMicIcon: boolean;

  // Feature flags
  allowLiveMic: boolean;
  defaultLiveMic: boolean;
  showPlayPause: boolean;

  // Component variants
  micModeToggleVariant: MicModeToggleVariant;
  waveformMode: WaveformMode;
}

export const GUI_THEMES_CONFIG = {
  original: {
    micStaticText: null,
    micVolatile: true,
    showMicIcon: false,
    allowLiveMic: false,
    defaultLiveMic: false,
    showPlayPause: false,
    micModeToggleVariant: 'switch',
    waveformMode: 'overlay',
  },
  arcade: {
    micStaticText: "Press and Hold to Talk",
    micVolatile: false,
    showMicIcon: true,
    allowLiveMic: false,
    defaultLiveMic: false,
    showPlayPause: false,
    micModeToggleVariant: 'switch',
    waveformMode: 'overlay',
  },
  complex1: {
    micStaticText: null,
    micVolatile: true,
    showMicIcon: false,
    allowLiveMic: true,
    defaultLiveMic: false,
    showPlayPause: true,
    micModeToggleVariant: 'button',
    waveformMode: 'replace',
  },
  complex2: {
    micStaticText: null,
    micVolatile: true,
    showMicIcon: false,
    allowLiveMic: true,
    defaultLiveMic: true,
    showPlayPause: true,
    micModeToggleVariant: 'button',
    waveformMode: 'replace',
  },
  complex3: {
    micStaticText: null,
    micVolatile: true,
    showMicIcon: false,
    allowLiveMic: true,
    defaultLiveMic: true,
    showPlayPause: true,
    micModeToggleVariant: 'button',
    waveformMode: 'replace',
  },
} as const satisfies Record<string, GUIThemeProperties>;

// Derive the GUITheme type from the config object keys
const GUI_THEME_KEYS = Object.keys(GUI_THEMES_CONFIG) as Array<keyof typeof GUI_THEMES_CONFIG>;
export type GUITheme = typeof GUI_THEME_KEYS[number];

// Helper to get all available GUI styles
export function getAvailableGUIThemes(): GUITheme[] {
  return GUI_THEME_KEYS as GUITheme[];
}

// Type guard to check if a string is a valid GUITheme
export function isValidGUITheme(theme: string | null | undefined): theme is GUITheme {
  if (!theme) return false;
  return theme in GUI_THEMES_CONFIG;
}

// Get GUI style properties with fallback
export function getGUIThemeProperties(style: GUITheme): GUIThemeProperties {
  const props = GUI_THEMES_CONFIG[style];
  if (props) {
    return props;
  }

  // Fallback to original if style not found
  return GUI_THEMES_CONFIG['original'];
}

// ─────────────────── Display Mode Configuration ───────────────────

/**
 * Valid display modes - must stay in sync with GUIThemeConfig.DisplayModeValue in backend
 *
 * - "fit": Aspect fit mode - maintains 1920x1080 aspect ratio regardless of viewport size.
 *          Server receives fixed 1920x1080 dimensions. Best for consistent quality.
 *
 * - "fill": Aspect fill mode - fills entire viewport. Server receives actual viewport
 *           dimensions. Best for mobile portrait to avoid letterboxing.
 *
 * - "reres": Re-resolution mode - sends actual viewport dimensions to server, and server
 *            dynamically adjusts its output resolution to match. Provides optimal quality
 *            for the actual displayed size. Used when dynamic quality scaling is desired.
 */
export const VALID_DISPLAY_MODES = ["fit", "fill", "reres"] as const;

/**
 * Display mode type - derived from VALID_DISPLAY_MODES for DRY principle.
 * Single source of truth for both runtime validation and compile-time types.
 */
export type DisplayMode = typeof VALID_DISPLAY_MODES[number];

/**
 * Type guard to check if a string is a valid DisplayMode.
 * Backend validates and sanitizes values, but we double-check here.
 */
export function isValidDisplayMode(mode: string | null | undefined): mode is DisplayMode {
  if (!mode) return false;
  return VALID_DISPLAY_MODES.includes(mode as DisplayMode);
}

export interface AppConfig {
  tenantName: string;
  enableStytch: boolean;
  enableNativeBridge: boolean;
  showUserLoginStatus: boolean;
  enableSidePanel: boolean;
  enableStandardFeedbackButton: boolean;

  guiTheme: GUITheme;
}

export const defaultAppConfig: AppConfig = {
  tenantName: 'Persona',
  enableStytch: true,
  enableNativeBridge: true,
  showUserLoginStatus: true,
  enableSidePanel: true,
  enableStandardFeedbackButton: true,

  guiTheme: 'original',
};
